import { ProductCombobox } from '@shared/components/ui/ProductCombobox';
import type { ProductOption } from '@shared/components/ui/ProductCombobox';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useProductSearch } from '@shared/hooks/useProductSearch';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// 🎯 Tipo para el producto seleccionado
export interface SelectedProduct {
  id: number;
  name: string;
  code?: string;
  image?: string; // 🎯 Campo unificado para imágenes
}

interface ProductSelectorProps {
  onProductSelect: (product: SelectedProduct) => void;
  placeholder?: string;
  className?: string;
  hideHelpText?: boolean;
}

export const ProductSelector = ({
  onProductSelect,
  placeholder,
  className = '',
  hideHelpText,
}: ProductSelectorProps) => {
  const { t } = useTranslation('locationsModule');
  const [selectedProductId, setSelectedProductId] = useState('');

  // 🎯 Placeholder con fallback a traducción
  const finalPlaceholder = placeholder || t('product_selector.default_placeholder');

  // 🎯 Hook de búsqueda de productos
  const { searchProductsForCombobox, totalProducts } = useProductSearch();

  // 🎯 Handler cuando se selecciona un producto
  const handleProductChange = (productId: string, selectedProduct: ProductOption | null) => {
    setSelectedProductId(productId);

    if (selectedProduct) {
      // Llamar al callback del componente padre
      onProductSelect({
        id: selectedProduct.id,
        name: selectedProduct.name,
        code: selectedProduct.code,
        image: selectedProduct.image,
      });
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 🎯 ProductCombobox con imagen, nombre y código */}
      <ProductCombobox
        value={selectedProductId}
        setValue={(value) => setSelectedProductId(value)}
        placeholder={finalPlaceholder}
        searchFn={searchProductsForCombobox}
        dataTestId="location-product-selector"
        onOptionSelected={(product) => {
          if (product) {
            handleProductChange(product.value, product);
          }
        }}
      />

      {/* 🎯 Texto de ayuda cuando no hay selección */}
      {!selectedProductId && !hideHelpText && (
        <div className="flex flex-col items-center justify-center pb-6 pt-4 px-6 rounded-xl ">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-hover-press text-icons rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faSearch} className="w-3 h-3" />
            </div>
            <h3 className="text-base font-medium">{t('product_selector.search_products_title')}</h3>
          </div>
          <p className="text-sm text-center max-w-xs">
            {t('product_selector.search_description', { count: totalProducts })}
          </p>
        </div>
      )}
    </div>
  );
};
